import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dayInReviewService,
  CreateDayInReviewDto,
  UpdateDayInReviewDto,
  CreateDecisionDto,
  EvaluateDecisionDto,
} from '../services/day-in-review.service';

// Query hooks
export function useDayInReviews() {
  return useQuery({
    queryKey: ['dayInReviews'],
    queryFn: () => dayInReviewService.getAll(),
  });
}

export function useDayInReview(id: string) {
  return useQuery({
    queryKey: ['dayInReviews', id],
    queryFn: () => dayInReviewService.getById(id),
    enabled: !!id,
  });
}

export function useDayInReviewByDate(date: string) {
  return useQuery({
    queryKey: ['dayInReviews', 'byDate', date],
    queryFn: () => dayInReviewService.getByDate(date),
    enabled: !!date,
  });
}

export function useChangesSummary(id: string) {
  return useQuery({
    queryKey: ['dayInReviews', id, 'changesSummary'],
    queryFn: () => dayInReviewService.getChangesSummary(id),
    enabled: !!id,
  });
}

export function useForecastSummary(id: string) {
  return useQuery({
    queryKey: ['dayInReviews', id, 'forecastSummary'],
    queryFn: () => dayInReviewService.getForecastSummary(id),
    enabled: !!id,
  });
}

export function useStaffingSummary(id: string) {
  return useQuery({
    queryKey: ['dayInReviews', id, 'staffingSummary'],
    queryFn: () => dayInReviewService.getStaffingSummary(id),
    enabled: !!id,
  });
}

export function useAnomalies(id: string) {
  return useQuery({
    queryKey: ['dayInReviews', id, 'anomalies'],
    queryFn: () => dayInReviewService.getAnomalies(id),
    enabled: !!id,
  });
}

export function useShrinkageComparison(id: string, expectedShrinkage?: number) {
  return useQuery({
    queryKey: ['dayInReviews', id, 'shrinkageComparison', expectedShrinkage],
    queryFn: () => dayInReviewService.getShrinkageComparison(id, expectedShrinkage),
    enabled: !!id,
  });
}

export function useLeaderHistory(userId: string) {
  return useQuery({
    queryKey: ['dayInReviews', 'leaderHistory', userId],
    queryFn: () => dayInReviewService.getLeaderHistory(userId),
    enabled: !!userId,
  });
}

// Mutation hooks
export function useGenerateDayInReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateDayInReviewDto) => dayInReviewService.generate(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews'] });
    },
  });
}

export function useRegenerateDayInReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => dayInReviewService.regenerate(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews'] });
      queryClient.invalidateQueries({ queryKey: ['dayInReviews', id] });
    },
  });
}

export function useUpdateDayInReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateDayInReviewDto }) =>
      dayInReviewService.update(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews'] });
      queryClient.invalidateQueries({ queryKey: ['dayInReviews', id] });
    },
  });
}

export function useCompleteDayInReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => dayInReviewService.complete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews'] });
      queryClient.invalidateQueries({ queryKey: ['dayInReviews', id] });
    },
  });
}

export function useDeleteDayInReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => dayInReviewService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews'] });
    },
  });
}

export function useAssignLeader() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, leaderOfDayId }: { id: string; leaderOfDayId: string }) =>
      dayInReviewService.assignLeader(id, leaderOfDayId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews', id] });
    },
  });
}

export function useEvaluateLeader() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rating, notes }: { id: string; rating: number; notes?: string }) =>
      dayInReviewService.evaluateLeader(id, rating, notes),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews', id] });
    },
  });
}

export function useAddDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, dto }: { reviewId: string; dto: CreateDecisionDto }) =>
      dayInReviewService.addDecision(reviewId, dto),
    onSuccess: (_data, { reviewId }) => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews', reviewId] });
    },
  });
}

export function useEvaluateDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reviewId,
      decisionId,
      dto,
    }: {
      reviewId: string;
      decisionId: string;
      dto: EvaluateDecisionDto;
    }) => dayInReviewService.evaluateDecision(reviewId, decisionId, dto),
    onSuccess: (_data, { reviewId }) => {
      queryClient.invalidateQueries({ queryKey: ['dayInReviews', reviewId] });
    },
  });
}
